/**
 * Puzzle Game Mode (Dr. Mario Style)
 * 
 * Features capsule-based treatment system with virus elimination mechanics.
 * Players drop two-cell capsules to create lines of 3+ matching cells,
 * which clear adjacent cancer cells (viruses).
 * 
 * Goal: Eliminate all cancer cells on the board to complete each level.
 * 
 * Key Features:
 * - Capsule dropping and rotation (two connected cells)
 * - Line-clearing mechanics (3+ in a row)
 * - Capsule breaking when one half is cleared
 * - 20 progressive levels with increasing difficulty
 * - Faster fall speed each level
 */

import { BaseGameMode, GameState, ModeConfig } from '../core/BaseGameMode'
import { Grid } from '../Grid'
import { BaseMatchingEngine, MatchResult } from '../core/BaseMatchingEngine'
import { FallingPair, PairGenerator, PairConfiguration } from '../FallingPair'
import { GameBalanceManager } from '../GameBalanceManager'
import { Cell, CellType, CellUtils } from '../CellTypes'
import { SettingsManager } from '../SettingsManager'
import { Logger } from '../../utils/Logger';


/**
 * Puzzle mode specific configuration
 */
export interface PuzzleConfig extends ModeConfig {
    maxLevel: number // Maximum level number (default 20)
    baseFallSpeed: number // Base fall speed in pixels/second (default 90)
    speedIncreasePerLevel: number // Speed increase per level
}

/**
 * Level information for puzzle mode
 */
interface LevelInfo {
    levelNumber: number
    virusCount: number
    virusPattern: VirusPattern
}

/**
 * Pattern type for virus placement
 */
type VirusPattern = 'random' | 'cluster' | 'horizontal' | 'vertical' | 'mixed'

export class PuzzleGameMode extends BaseGameMode {
    // Puzzle mode specific state
    private nextPairs: PairConfiguration[]
    private callbacks: Map<string, Function[]>
    private currentLevelInfo: LevelInfo
    private remainingViruses: number
    private totalVirusesInLevel: number

    constructor(
        grid: Grid,
        matchingEngine: BaseMatchingEngine,
        pairGenerator: PairGenerator,
        balanceManager: GameBalanceManager,
        config: PuzzleConfig
    ) {
        super(grid, matchingEngine, pairGenerator, balanceManager, config)

        // Set pair generator to medical mode IMMEDIATELY in constructor
        this.pairGenerator.setGameMode('medical')

        this.nextPairs = []
        this.callbacks = new Map()
        this.currentLevelInfo = { levelNumber: 1, virusCount: 3, virusPattern: 'random' }
        this.remainingViruses = 0
        this.totalVirusesInLevel = 0
    }

    /**
     * Initialize Puzzle mode - set up first level
     */
    public initialize(): void {
        // Use settings to determine starting level
        const settings = SettingsManager.getInstance()
        const startingLevel = settings.getPuzzleModeLevel()
        this.stats.level = startingLevel

        this.setupLevel(this.stats.level)
        this.generateNextPairs()
        this.spawnNextPair()
        this.emit('gameStarted', this.stats)
    }    /**
     * Update game state based on delta time
     */
    public update(deltaTime: number): void {
        if (
            this.gameState === GameState.PAUSED ||
            this.gameState === GameState.WAITING_TO_START ||
            this.gameState === GameState.GAME_OVER ||
            this.gameState === GameState.VICTORY
        ) {
            return
        }

        this.updateTimeStats()

        switch (this.gameState) {
            case GameState.PLAYING:
                this.updatePlaying(deltaTime)
                break
            case GameState.FALLING:
                this.updateFalling(deltaTime)
                break
            case GameState.MATCHING:
                this.updateMatching()
                break
        }
    }

    /**
     * Handle player input
     */
    public handleInput(action: string): boolean {
        // Handle pause/unpause regardless of game state
        if (action === 'pause') {
            this.pauseGame()
            return true
        }

        if (!this.currentPair || this.gameState !== GameState.PLAYING) {
            return false
        }

        switch (action) {
            case 'moveLeft':
                return this.currentPair.moveHorizontal(-1)
            case 'moveRight':
                return this.currentPair.moveHorizontal(1)
            case 'rotateClockwise':
                return this.currentPair.rotate(1)
            case 'rotateCounterClockwise':
                return this.currentPair.rotate(-1)
            case 'softDrop':
                return this.currentPair.moveDown()
            case 'hardDrop':
                this.currentPair.hardDrop()
                return true
            default:
                return false
        }
    }

    /**
     * Pause the game and emit event
     */
    public pauseGame(): void {
        super.pauseGame()
        if (this.gameState === GameState.PAUSED) {
            this.emit('gamePaused')
        }
    }

    /**
     * Resume the game and emit event
     */
    public resumeGame(): void {
        super.resumeGame()
        if (this.gameState === GameState.PLAYING) {
            this.emit('gameResumed')
        }
    }

    /**
     * Get preview of next pairs
     */
    public getNextPairs(): PairConfiguration[] {
        return [...this.nextPairs]
    }

    /**
     * Get remaining virus count
     */
    public getRemainingViruses(): number {
        return this.remainingViruses
    }

    /**
     * Get total viruses in current level
     */
    public getTotalVirusesInLevel(): number {
        return this.totalVirusesInLevel
    }

    /**
     * Register event callbacks
     */
    public on(event: string, callback: Function): void {
        if (!this.callbacks.has(event)) {
            this.callbacks.set(event, [])
        }
        this.callbacks.get(event)!.push(callback)
    }

    /**
     * Remove event callbacks
     */
    public off(event: string, callback?: Function): void {
        if (!this.callbacks.has(event)) return

        if (callback) {
            const callbacks = this.callbacks.get(event)!
            const index = callbacks.indexOf(callback)
            if (index > -1) {
                callbacks.splice(index, 1)
            }
        } else {
            this.callbacks.set(event, [])
        }
    }

    // ========================================
    // Protected Methods (Mode-Specific Logic)
    // ========================================

    /**
     * Check if game should end - Victory when all viruses cleared, Game Over if grid overflows
     */
    protected checkEndConditions(): void {
        // Check for game over (grid overflow)
        if (this.grid.isOverflowing()) {
            this.gameState = GameState.GAME_OVER
            this.emit('gameOver', this.stats)
            return
        }

        // Check for victory (all viruses cleared)
        this.remainingViruses = this.countRemainingViruses()
        if (this.remainingViruses === 0) {
            // Level complete!
            const puzzleConfig = this.config as PuzzleConfig
            const maxLevel = puzzleConfig.maxLevel || 20

            if (this.stats.level >= maxLevel) {
                // Game complete!
                this.gameState = GameState.VICTORY
                this.emit('gameComplete', this.stats)
            } else {
                // Advance to next level
                this.stats.level++
                this.setupLevel(this.stats.level)
                this.emit('levelComplete', {
                    level: this.stats.level - 1,
                    nextLevel: this.stats.level
                })
            }
        }
    }

    /**
     * Calculate score for cleared cells with chain multiplier
     */
    protected calculateScore(cellsCleared: number, chainLevel: number): number {
        const baseScore = cellsCleared * 10
        const chainMultiplier = chainLevel || 1
        return baseScore * chainMultiplier
    }

    // ========================================
    // Private Methods (Update Loop)
    // ========================================

    /**
     * Update the playing state
     */
    private updatePlaying(deltaTime: number): void {
        if (!this.currentPair) {
            this.spawnNextPair()
            return
        }

        const stillFalling = this.currentPair.update(deltaTime)

        if (!stillFalling) {
            // Pair has locked
            this.stats.pairsDropped++
            this.currentPair = null
            this.gameState = GameState.MATCHING
            this.emit('pairLocked')
        }
    }

    /**
     * Update the falling state (cells falling due to gravity)
     * Uses capsule-aware gravity for Dr. Mario mode
     */
    private updateFalling(deltaTime: number): void {
        const cellsMoved = this.grid.applyGravityWithCapsules()

        if (!cellsMoved) {
            // All cells have settled, check for new matches
            this.gameState = GameState.MATCHING
        }
    }

    /**
     * Update the matching state (stepwise cascade).
     * Each call processes one chain step. If matches are found and
     * more are pending, transitions to FALLING. Otherwise completes
     * the cascade and spawns the next pair.
     */
    private updateMatching(): void {
        const result = this.matchingEngine.processMatches()

        if (result.totalCellsCleared > 0) {
            // Process match results from this step
            this.processMatchResult(result)

            if (result.hasMore) {
                this.gameState = GameState.FALLING
                this.emit('cascadeStepComplete', result)
            } else {
                this.completeCascadeResolution()
            }
        } else {
            this.completeCascadeResolution()
        }
    }

    /**
     * Handles the end of a full cascade resolution.
     * Resets the chain, checks game end conditions, and spawns the next pair.
     */
    private completeCascadeResolution(): void {
        this.matchingEngine.resetChain()
        this.checkEndConditions()

        if (this.gameState !== GameState.GAME_OVER && this.gameState !== GameState.VICTORY) {
            this.gameState = GameState.PLAYING
            this.spawnNextPair()
        }
    }

    /**
     * Spawn the next falling pair (capsule)
     */
    private spawnNextPair(): void {
        // Ensure we always have at least 2 pairs in the queue for preview
        if (this.nextPairs.length < 2) {
            this.generateNextPairs()
        }

        const nextConfig = this.nextPairs.shift()!
        this.currentPair = new FallingPair(nextConfig, this.grid, 2, 'medical')

        // Set fall speed based on settings and current level
        const settings = SettingsManager.getInstance()
        const speedSetting = settings.getPuzzleModeSpeed()

        // Define base fall speeds for different settings
        const baseSpeeds = {
            'Low': 60,   // Slow for beginners
            'Med': 90,   // Medium speed (original default)
            'Hi': 120    // Fast for experienced players
        }

        const baseFallSpeed = baseSpeeds[speedSetting]
        const speedIncrease = Math.floor(baseFallSpeed * 0.05) // 5% increase per level
        const fallSpeed = baseFallSpeed + (this.stats.level - 1) * speedIncrease
        this.currentPair.setFallSpeed(1000 / (fallSpeed / 60)) // Convert pixels/second to ms per cell

        // Check if the new pair can be placed
        if (this.currentPair.isLocked()) {
            // Grid is full, game over
            this.gameState = GameState.GAME_OVER
            this.emit('gameOver', this.stats)
        } else {
            this.emit('pairSpawned', nextConfig)
        }
    }

    /**
     * Generate the next set of capsule pairs
     */
    private generateNextPairs(): void {
        // In puzzle mode, generate capsules from medical cell types only
        // No cancer cells in capsules, only RBC, WBC, Stem, and Donor cells
        this.pairGenerator.adjustWeights(0, this.stats.level)

        // Generate preview pairs
        this.nextPairs = this.pairGenerator.previewNext(3)
    }

    /**
     * Process the result of matches
     */
    private processMatchResult(result: MatchResult): void {
        this.stats.score += result.scoreGained
        this.stats.totalCellsCleared += result.totalCellsCleared
        this.stats.longestChain = Math.max(this.stats.longestChain, result.chainLevel)

        // Update balance manager with match data
        this.balanceManager.recordMatchResult({
            score: result.scoreGained,
            chainLevel: result.chainLevel,
            cellsCleared: result.totalCellsCleared,
            level: this.stats.level
        })

        this.emit('matchProcessed', result)
    }

    /**
     * Set up a new level with viruses
     */
    private setupLevel(levelNumber: number): void {
        this.grid.clear()

        // Calculate virus count with reasonable scaling for 6×12 grid (72 squares)
        // Cancer cells only placed in bottom third (4 rows × 6 cols = 24 squares max)
        // Formula: Start at 4, add 1 per level, cap at 20 viruses (83% of bottom third)
        // Level 0: 4, Level 1: 5, Level 5: 9, Level 10: 14, Level 16+: 20 viruses max
        const effectiveLevel = Math.min(levelNumber, 16)
        const virusCount = Math.min(4 + effectiveLevel, 20)

        // Determine pattern based on level
        const pattern = this.getLevelPattern(levelNumber)

        this.currentLevelInfo = {
            levelNumber,
            virusCount,
            virusPattern: pattern
        }

        // Place viruses on the grid
        this.placeViruses(virusCount, pattern)

        this.remainingViruses = virusCount
        this.totalVirusesInLevel = virusCount

        this.emit('levelSetup', this.currentLevelInfo)
    }

    /**
     * Get the virus pattern for a given level
     */
    private getLevelPattern(level: number): VirusPattern {
        // Early levels use simple patterns, later levels use more complex ones
        if (level <= 5) return 'random'
        if (level <= 10) return 'cluster'
        if (level <= 15) return 'horizontal'
        return 'mixed'
    }

    /**
     * Place viruses on the grid according to pattern
     */
    private placeViruses(count: number, pattern: VirusPattern): void {
        const placedViruses = []
        const maxAttempts = count * 10
        let attempts = 0

        // Define bottom two-thirds of grid (Grid.HEIGHT = 12, so bottom 2/3 is rows 4-11)
        // This gives 8 rows × 6 cols = 48 available squares for virus placement
        const virusAreaStart = Math.floor(Grid.HEIGHT / 3) // Row 4 (leave top 1/3 clear)
        const virusAreaEnd = Grid.HEIGHT - 1 // Row 11

        while (placedViruses.length < count && attempts < maxAttempts) {
            attempts++

            let x, y

            // Select position based on pattern
            switch (pattern) {
                case 'random':
                    x = Math.floor(Math.random() * Grid.WIDTH)
                    y = Math.floor(Math.random() * (virusAreaEnd - virusAreaStart + 1)) + virusAreaStart
                    break

                case 'cluster':
                    // Place viruses in the center area
                    x = Math.floor(Math.random() * 4) + 1 // Columns 1-4
                    y = Math.floor(Math.random() * (virusAreaEnd - virusAreaStart + 1)) + virusAreaStart
                    break

                case 'horizontal':
                    // Place viruses in horizontal lines, working up from bottom
                    x = Math.floor(Math.random() * Grid.WIDTH)
                    y = Math.max(virusAreaStart, virusAreaEnd - Math.floor(placedViruses.length / 3)) // Start from bottom and work up
                    break

                case 'vertical':
                    // Place viruses in vertical columns
                    x = 2 + Math.floor(placedViruses.length % 2) // Columns 2-3
                    y = Math.floor(Math.random() * (virusAreaEnd - virusAreaStart + 1)) + virusAreaStart
                    break

                case 'mixed':
                default:
                    // Mixed pattern for higher levels
                    if (placedViruses.length < count / 2) {
                        // First half: cluster
                        x = Math.floor(Math.random() * 4) + 1
                        y = Math.floor(Math.random() * (virusAreaEnd - virusAreaStart + 1)) + virusAreaStart
                    } else {
                        // Second half: random
                        x = Math.floor(Math.random() * Grid.WIDTH)
                        y = Math.floor(Math.random() * (virusAreaEnd - virusAreaStart + 1)) + virusAreaStart
                    }
                    break
            }

            // Check if position is empty
            if (this.grid.isEmpty(x, y)) {
                // Create and place cancer cell
                const cancerCell = CellUtils.createCell(CellType.CANCER, x, y)
                if (this.grid.placeCell(cancerCell, x, y)) {
                    placedViruses.push(cancerCell)
                }
            }
        }

        Logger.getInstance().log(`Placed ${placedViruses.length}/${count} cancer cells in bottom two-thirds (rows ${virusAreaStart}-${virusAreaEnd}) for level ${this.currentLevelInfo.levelNumber}`)
    }

    /**
     * Count remaining viruses on the grid
     */
    private countRemainingViruses(): number {
        let count = 0
        for (let y = 0; y < Grid.HEIGHT; y++) {
            for (let x = 0; x < Grid.WIDTH; x++) {
                const cell = this.grid.getCell(x, y)
                if (cell && this.isCancerCell(cell)) {
                    count++
                }
            }
        }
        return count
    }

    /**
     * Check if a cell is a cancer cell (virus)
     */
    private isCancerCell(cell: Cell): boolean {
        return cell.type === CellType.CANCER
    }

    /**
     * Emit events to registered callbacks
     */
    protected emit(event: string, data?: any): void {
        const callbacks = this.callbacks.get(event)
        if (callbacks) {
            callbacks.forEach(callback => callback(data))
        }
    }
}
